/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import React from 'react'
import { Dropdown } from 'antd'
import type { MenuProps } from 'antd'
import {
  MoreOutlined,
  EditOutlined,
  DeleteOutlined,
  ControlFilled,
} from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { WEEK_DAYS } from '@sportninja/common/constants/weekDays'
import { TIMES_OF_DAY } from '@sportninja/common/constants/timesOfDay'
import type { FinderPreference } from '@sportninja/common/features/profile/finderSettings/types'
import { savedForCompetitionsLabel } from '@sportninja/common/features/profile/finderSettings/savedForLabel'
import { finderLocationLabel } from '@sportninja/common/features/profile/finderSettings/locationLabel'
import { getSkillLevelColor } from '@sportninja/common/features/playerFinder/skillLevelColor'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import {
  font,
  weightSemiBold,
  weightBold,
  paragraphSmall,
  paragraphLarge,
  paragraphXSmall,
} from 'src/components/css'
import SkillLevelIcon from 'src/components/icons/SkillLevelIcon'
import { useSportName } from '../hooks/useSportName'
import FinderPreferenceCompetitions from './FinderPreferenceCompetitions'
import FinderNoCompetitions from './FinderNoCompetitions'

interface Props {
  preference: FinderPreference
  onEdit: (preference: FinderPreference) => void
  onRemove: (preference: FinderPreference) => void
}

const formatRate = (rate: number): string => String(Number(rate.toFixed(2)))

const daysLabel = (pref: FinderPreference): string => {
  if (pref.available_days.length === 0) {
    return '—'
  }
  const indexOf = (day: string) =>
    WEEK_DAYS.find((w) => w.value === day)?.index ?? 0
  return [...pref.available_days]
    .sort((a, b) => indexOf(a) - indexOf(b))
    .map((d) => WEEK_DAYS.find((w) => w.value === d)?.short ?? d)
    .join(', ')
}

const timesLabel = (pref: FinderPreference): string => {
  const times = pref.available_times ?? []
  if (times.length === 0) {
    return '—'
  }
  const indexOf = (time: string) =>
    TIMES_OF_DAY.find((t) => t.value === time)?.index ?? 0
  return [...times].sort((a, b) => indexOf(a) - indexOf(b)).join(', ')
}

/**
 * Single finder-preference card: sport name + ⋮ overflow menu (Edit/Remove),
 * position/skill/rate meta row, available days/times, bio, and the
 * competitions it's saved for (links) — mirrors
 * `FamilyMemberRow`'s card treatment and Dropdown menu pattern.
 */
const FinderPreferenceCard: React.FC<Props> = ({
  preference,
  onEdit,
  onRemove,
}) => {
  const sportName = useSportName(preference)
  const skillColor = getSkillLevelColor(preference.skill_level)
  // SN-8295 — this card's own location, not the profile address.
  const locationLabel = finderLocationLabel(preference.location)

  const menuItems: MenuProps['items'] = [
    {
      key: 'edit',
      icon: <EditOutlined />,
      label: capitalizeWords('Edit'),
      onClick: () => onEdit(preference),
    },
    {
      key: 'remove',
      icon: <DeleteOutlined />,
      label: capitalizeWords('Remove'),
      danger: true,
      onClick: () => onRemove(preference),
    },
  ]

  return (
    <div css={cardStyle}>
      <div css={headerStyle}>
        <div css={headerLeftStyle}>
          {/* Sized to the sport name's line, so the icon sits on the title
              rather than centred between it and the "Saved for" line. */}
          <span css={sportIconStyle}>
            <ControlFilled aria-hidden />
          </span>
          <div css={sportTitleStyle}>
            <span css={sportNameStyle}>{capitalizeWords(sportName)}</span>
            {/* SN-8299 — one card per sport, saved as a copy per competition. */}
            <span css={savedForStyle}>
              {savedForCompetitionsLabel(preference.competitions_count)}
            </span>
          </div>
        </div>
        <Dropdown
          menu={{ items: menuItems }}
          trigger={['click']}
          placement='bottomRight'
          /* Render above modal wrappers (zIndex 10001) — mirrors FamilyMemberRow. */
          overlayStyle={{ zIndex: 10050 }}
        >
          <button
            type='button'
            css={moreButtonStyle}
            aria-label={`More options for ${sportName}`}
          >
            <MoreOutlined css={moreIconStyle} />
          </button>
        </Dropdown>
      </div>

      <div css={dividerStyle} />

      <div css={metaRowStyle}>
        <div css={metaItemStyle}>
          <div css={metaTitleStyle}>{capitalizeWords('Position')}</div>
          <div css={metaValueStyle}>{preference.position_name || '—'}</div>
        </div>
        <div css={metaItemStyle}>
          <div css={metaTitleStyle}>{capitalizeWords('Skill Level')}</div>
          <div
            css={[
              metaValueStyle,
              skillChipStyle,
              skillColor &&
                css`
                  color: ${skillColor};
                `,
            ]}
          >
            {/* Same growth-ramp glyph the Player Finder cards use, tinted by
                the skill's own colour. Only shown when a level is actually
                set — no icon beside the em-dash placeholder. */}
            {preference.skill_level ? (
              <SkillLevelIcon
                skillLevel={preference.skill_level}
                size={14}
                color={skillColor ?? colors.NEUTRAL_50}
              />
            ) : null}
            {preference.skill_level || '—'}
          </div>
        </div>
        {preference.charge_rate != null ? (
          <div css={rateItemStyle}>
            <div css={metaTitleStyle}>{capitalizeWords('Rate')}</div>
            <div css={rateValueStyle}>
              ${formatRate(preference.charge_rate)}
            </div>
            <div css={rateSuffixStyle}>per game</div>
          </div>
        ) : null}
      </div>

      {locationLabel ? (
        <>
          <div css={dividerStyle} />
          <div>
            <div css={metaTitleStyle}>{capitalizeWords('Location')}</div>
            <div css={metaValueStyle}>{locationLabel}</div>
          </div>
        </>
      ) : null}

      {preference.available_days.length > 0 ? (
        <>
          <div css={dividerStyle} />
          <div>
            <div css={metaTitleStyle}>{capitalizeWords('Available Days')}</div>
            <div css={metaValueStyle}>{daysLabel(preference)}</div>
          </div>
        </>
      ) : null}

      {(preference.available_times ?? []).length > 0 ? (
        <>
          <div css={dividerStyle} />
          <div>
            <div css={metaTitleStyle}>{capitalizeWords('Available Times')}</div>
            <div css={metaValueStyle}>{timesLabel(preference)}</div>
          </div>
        </>
      ) : null}

      {preference.bio ? (
        <>
          <div css={dividerStyle} />
          <div>
            <div css={metaTitleStyle}>{capitalizeWords('Player Bio')}</div>
            <div css={bioStyle}>{preference.bio}</div>
          </div>
        </>
      ) : null}

      <div css={dividerStyle} />
      <div>
        <div css={metaTitleStyle}>{capitalizeWords('Competitions')}</div>
        {preference.competitions.length > 0 ? (
          <FinderPreferenceCompetitions
            competitions={preference.competitions}
          />
        ) : (
          <FinderNoCompetitions />
        )}
      </div>
    </div>
  )
}

export default FinderPreferenceCard

// ─── Styles ────────────────────────────────────────────────────────────────

const cardStyle = css`
  background-color: ${colors.SECONDARY_400};
  border-radius: 12px;
  border: 1px solid ${colors.NEUTRAL_700};
  padding: 16px;
`

const headerStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
`

const headerLeftStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
  flex: 1;
`

/* Same preset as the sport name, so the icon's line box matches the title's. */
const sportIconStyle = css`
  ${paragraphLarge}
  display: block;
  color: ${colors.PRIMARY};
  flex-shrink: 0;
`

/* Sport name over its "Saved for N competitions" line. */
const sportTitleStyle = css`
  display: flex;
  flex-direction: column;
  min-width: 0;
`

const savedForStyle = css`
  ${paragraphXSmall}
  color: ${colors.NEUTRAL_50};
`

const sportNameStyle = css`
  ${paragraphLarge}
  ${weightBold}
  ${font.body}
  color: ${colors.WHITE};
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`

const moreButtonStyle = css`
  background: transparent;
  border: none;
  padding: 0;
  cursor: pointer;
  line-height: 0;
  display: flex;
  align-items: center;
`

const moreIconStyle = css`
  font-size: 22px;
  color: ${colors.PRIMARY};
`

const dividerStyle = css`
  height: 1px;
  background-color: ${colors.SOFT_STEEL};
  margin: 12px 0;
`

const metaRowStyle = css`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`

const metaItemStyle = css`
  flex: 1;
  min-width: 0;
`

const metaTitleStyle = css`
  ${paragraphSmall}
  ${weightSemiBold}
  ${font.body}
  color: ${colors.NEUTRAL_50};
  margin-bottom: 3px;
`

const metaValueStyle = css`
  ${paragraphSmall}
  ${font.body}
  color: ${colors.NEUTRAL_0};
`

/* Keeps the skill glyph and its label on one line. */
const skillChipStyle = css`
  display: flex;
  align-items: center;
  gap: 4px;
`

const rateItemStyle = css`
  text-align: right;
`

const rateValueStyle = css`
  ${font.title}
  ${weightBold}
  font-size: 20px;
  letter-spacing: 1px;
  color: ${colors.SUCCESS_300};
`

const rateSuffixStyle = css`
  ${font.body}
  font-size: 11px;
  color: ${colors.NEUTRAL_50};
  margin-top: 2px;
`

const bioStyle = css`
  ${paragraphSmall}
  ${font.body}
  color: ${colors.NEUTRAL_0};
`
